import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import {
  useFetchCategoryStatusCompanies,
  useSaveCategoryStatusCompanies,
  useFetchCategoryStatusUserSettings,
  useSaveCategoryStatusUserSettings,
} from "@lhd/hooks";
import {
  CategoryStatusCompany,
  CategoryStatusUserSettingItem,
  CategoryStatusUserSettingPayloadItem,
  SaveCategoryStatusUserSettingsPayload,
} from "@lhd/types";
import { notification } from "antd";
import { isEqual, isNumber, pick, uniqBy } from "lodash";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomFilterConfigs } from ".";
import { getIsCollapse as getIsCollapseSidebar } from "../CommonLayout/sidebarSelector";
import { getIsBannerVisible } from "../CommonLayout/sidebarSelector";
import { getIsCollapse } from "./searchAreaSelector";
import { setIsCollapse as setIsCollapseAction } from "./searchAreaSlice";
import { handleMessageError } from "@lhd/features/Apps/utils";
import { FILTER_STATUS, FilterSelected, MappedItem, Type } from "./types";
import { getDisplaySize } from "../DisplaySizeSelector/selector";
import {
  BUTTON_EDIT_MODE,
  BUTTON_HEIGHT,
  FONT_SIZE,
  QUERY_NAME,
  RECORD_TYPE,
  RecordType,
  SCREEN_NAME,
  SPECIAL_FILTER_VALUE,
  FILTER_STATUS_TYPE,
  WORKER_SETTING_MESSAGES,
  SPECIAL_FILTER_LABEL,
} from "@lhd/constants";
import { getCompanyId } from "@lhd/utility";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export type DefaultFilterOption = {
  text: string;
  value: string;
  id?: string | number;
  is_auto_select?: boolean;
  is_hidden?: boolean;
  [key: string]: any;
};

export type ReceivedProps = {
  isOpen?: boolean;
  onCancel?: () => void;
  onOk?: (listSelected: string[]) => void;
  tableName: string;
  screen?: string;
  optionDefaultStatus?: DefaultFilterOption[];
  optionDefaultCategory?: DefaultFilterOption[];
  fieldStatus: string;
  fieldCategory: string;
  handleChangeStatus?: (val: string) => void;
  statusFilterSelected: FilterSelected[];
  setStatusFilterSelected?: Dispatch<SetStateAction<FilterSelected[]>>;
  handleChangeCategory?: (val: string) => void;
  categoryFilterSelected: FilterSelected[];
  setCategoryFilterSelected?: Dispatch<SetStateAction<FilterSelected[]>>;
  textStatusField: string;
  textCategoryField: string;
  disabled?: boolean;
  disabledClick?: boolean;
  nonEditable?: boolean;
  disabledButtonEdit?: boolean;
  chatCategories?: any[];
  chatStatuses?: any[];
  isLoadingChatCategories?: boolean;
  isLoadingChatStatuses?: boolean;
  disableButtonEditMode?: typeof BUTTON_EDIT_MODE[keyof typeof BUTTON_EDIT_MODE];
  forceCheckAllIfEmpty?: boolean;
  showConfigControls?: boolean;
  hasNullStatusOption?: boolean;
  categoryStatusParams?: Record<string, any>;
};

type ModalSettingConfigProps = {
  isOpen: boolean;
  type: RecordType | null;
};

const EMPTY_ARRAY: any[] = [];

const useSearchAreaBox = (props: ReceivedProps) => {
  const {
    optionDefaultStatus,
    optionDefaultCategory,
    setStatusFilterSelected,
    statusFilterSelected,
    setCategoryFilterSelected,
    categoryFilterSelected,
    screen,
    disabled,
    nonEditable,
    disabledButtonEdit,
    handleChangeStatus,
    handleChangeCategory,
    chatCategories: preFetchedChatCategories,
    chatStatuses: preFetchedChatStatuses,
    isLoadingChatCategories: preFetchedIsLoadingChatCategories,
    isLoadingChatStatuses: preFetchedIsLoadingChatStatuses,
    forceCheckAllIfEmpty,
    showConfigControls,
    hasNullStatusOption,
    categoryStatusParams,
  } = props;
  const [searchParams] = useSearchParams();
  const isDisplayInNewTab = searchParams.get("isDisplayInNewTab") === "true";
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isCollapse = useSelector(getIsCollapse);
  const isCollapseSidebar = useSelector(getIsCollapseSidebar);
  const isBannerVisible = useSelector(getIsBannerVisible);

  // Company context
  const companyId = Number(getCompanyId());
  const effectiveScreen =
    screen === SCREEN_NAME.LIST_MESS ? SCREEN_NAME.CHAT : (screen as string);

  // Tracking initialization and user modifications for saving to BE
  const isInitializedStatusRef = useRef(false);
  const isInitializedCategoryRef = useRef(false);
  const initialStatusAutoSelectRef = useRef<Record<string | number, boolean>>(
    {},
  );
  const initialCategoryAutoSelectRef = useRef<Record<string | number, boolean>>(
    {},
  );
  const hasUserModifiedStatusRef = useRef(false);
  const hasUserModifiedCategoryRef = useRef(false);
  const prevScreenRef = useRef<string | undefined>(screen);
  const statusDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const categoryDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const initialStatusSnapshotRef = useRef<string>("");
  const initialCategorySnapshotRef = useRef<string>("");

  if (screen !== prevScreenRef.current) {
    prevScreenRef.current = screen;
    isInitializedStatusRef.current = false;
    isInitializedCategoryRef.current = false;
    hasUserModifiedStatusRef.current = false;
    hasUserModifiedCategoryRef.current = false;
    initialStatusAutoSelectRef.current = {};
    initialCategoryAutoSelectRef.current = {};
    initialStatusSnapshotRef.current = "";
    initialCategorySnapshotRef.current = "";
    if (statusDebounceTimerRef.current) {
      clearTimeout(statusDebounceTimerRef.current);
    }
    if (categoryDebounceTimerRef.current) {
      clearTimeout(categoryDebounceTimerRef.current);
    }
  }

  const [listDataStatus, setListDataStatus] = useState<CustomFilterConfigs[]>(
    [],
  );
  const [listDataCategory, setListDataCategory] = useState<
    CustomFilterConfigs[]
  >([]);

  const safeSetListDataStatus = useCallback(
    (
      nextOrUpdater:
        | CustomFilterConfigs[]
        | ((prev: CustomFilterConfigs[]) => CustomFilterConfigs[]),
    ) => {
      setListDataStatus((prev) => {
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(prev)
            : nextOrUpdater;
        return isEqual(prev, next) ? prev : next;
      });
    },
    [],
  );

  const safeSetListDataCategory = useCallback(
    (
      nextOrUpdater:
        | CustomFilterConfigs[]
        | ((prev: CustomFilterConfigs[]) => CustomFilterConfigs[]),
    ) => {
      setListDataCategory((prev) => {
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(prev)
            : nextOrUpdater;
        return isEqual(prev, next) ? prev : next;
      });
    },
    [],
  );

  const [modalSettingConfig, setModalSettingConfig] =
    useState<ModalSettingConfigProps>({
      isOpen: false,
      type: null,
    });
  const [modalConfirm, setModalConfirm] = useState<{
    isOpen: boolean;
    callback: any;
    content: string;
  }>({
    isOpen: false,
    callback: null,
    content: "",
  });
  const [modalEditConfirm, setModalEditConfirm] = useState<{
    isOpen: boolean;
    onConfirm: (() => void) | null;
    onCancel: (() => void) | null;
  }>({
    isOpen: false,
    onConfirm: null,
    onCancel: null,
  });
  const [modalResetSignal, setModalResetSignal] = useState(0);
  const [maintenanceBannerHeight, setMaintenanceBannerHeight] =
    useState<number>(0);

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;

  // Get maintenance banner height
  useLayoutEffect(() => {
    const updateBannerHeight = () => {
      const banner = document.getElementById("maintenance-banner");
      const height = banner?.offsetHeight || 0;
      setMaintenanceBannerHeight(height);
    };

    updateBannerHeight();

    // Watch for banner changes
    const observer = new MutationObserver(updateBannerHeight);
    const header = document.getElementById("app-header");
    if (header) {
      observer.observe(header, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [isBannerVisible]);

  const isListMess =
    screen === SCREEN_NAME.LIST_MESS || screen === SCREEN_NAME.CHAT;
  const isFixedSlicerApp = screen === APP_TYPES.BUSINESS_FLOW;

  // 1. Fetch user-personalized settings when not provided via props
  const canFetchUserSettings = Boolean(
    !preFetchedChatStatuses &&
      !preFetchedChatCategories &&
      effectiveScreen &&
      companyId,
  );

  const { data: userStatusesData, isLoading: isLoadingUserStatuses } =
    useFetchCategoryStatusUserSettings(
      {
        company_id: companyId,
        screen: effectiveScreen,
        record_type: RECORD_TYPE.STATUS,
      },
      canFetchUserSettings,
    );

  const { data: userCategoriesData, isLoading: isLoadingUserCategories } =
    useFetchCategoryStatusUserSettings(
      {
        company_id: companyId,
        screen: effectiveScreen,
        record_type: RECORD_TYPE.CATEGORY,
      },
      canFetchUserSettings,
    );

  // Hook to mutate user settings
  const { mutate: saveUserSettingsMutation } =
    useSaveCategoryStatusUserSettings();

  // 2. Fetch company category_status_companies as fallback ONLY when user settings are not available
  const hasUserStatuses = Boolean(
    (preFetchedChatStatuses && preFetchedChatStatuses.length > 0) ||
      (userStatusesData && userStatusesData.length > 0),
  );
  const hasUserCategories = Boolean(
    (preFetchedChatCategories && preFetchedChatCategories.length > 0) ||
      (userCategoriesData && userCategoriesData.length > 0),
  );
  const shouldFetchCompanySettings =
    !isListMess &&
    !isFixedSlicerApp &&
    !canFetchUserSettings &&
    (!hasUserStatuses || !hasUserCategories);

  const {
    data: categoryStatusCompaniesData,
    isLoading: isLoadingCategoryStatusCompaniesData,
    isFetching: isFetchingCategoryStatusCompaniesData,
    isSuccess: isSuccessCategoryStatusCompaniesData,
  } = useFetchCategoryStatusCompanies(
    { screen, ...categoryStatusParams },
    shouldFetchCompanySettings,
  );

  const {
    mutate: saveCategoryStatusCompanies,
    isLoading: isSavingCategoryStatusCompanies,
  } = useSaveCategoryStatusCompanies();

  // Resolved statuses and categories combining props, user settings, and company defaults
  const resolvedStatuses: any[] = useMemo(() => {
    if (preFetchedChatStatuses && preFetchedChatStatuses.length > 0) {
      return preFetchedChatStatuses;
    }
    if (userStatusesData && userStatusesData.length > 0) {
      return userStatusesData;
    }
    const companyStatuses = categoryStatusCompaniesData?.data?.filter(
      (i: any) => i.record_type === RECORD_TYPE.STATUS,
    );
    if (companyStatuses && companyStatuses.length > 0) {
      return companyStatuses;
    }
    return EMPTY_ARRAY;
  }, [
    preFetchedChatStatuses,
    userStatusesData,
    categoryStatusCompaniesData?.data,
  ]);

  const resolvedCategories: any[] = useMemo(() => {
    if (preFetchedChatCategories && preFetchedChatCategories.length > 0) {
      return preFetchedChatCategories;
    }
    if (userCategoriesData && userCategoriesData.length > 0) {
      return userCategoriesData;
    }
    const companyCategories = categoryStatusCompaniesData?.data?.filter(
      (i: any) => i.record_type === RECORD_TYPE.CATEGORY,
    );
    if (companyCategories && companyCategories.length > 0) {
      return companyCategories;
    }
    return EMPTY_ARRAY;
  }, [
    preFetchedChatCategories,
    userCategoriesData,
    categoryStatusCompaniesData?.data,
  ]);

  const isStatusesLoading =
    preFetchedIsLoadingChatStatuses !== undefined
      ? preFetchedIsLoadingChatStatuses
      : canFetchUserSettings
      ? isLoadingUserStatuses
      : isLoadingCategoryStatusCompaniesData && !categoryStatusCompaniesData;

  const isCategoriesLoading =
    preFetchedIsLoadingChatCategories !== undefined
      ? preFetchedIsLoadingChatCategories
      : canFetchUserSettings
      ? isLoadingUserCategories
      : isLoadingCategoryStatusCompaniesData && !categoryStatusCompaniesData;

  // Memoized string keys for stable effect dependencies
  const statusesKey = useMemo(() => {
    if (!resolvedStatuses || resolvedStatuses.length === 0) return "";
    return JSON.stringify(
      resolvedStatuses.map((s: any) => [
        s.id,
        s.name,
        s.color,
        s.is_hidden,
        s.is_auto_select,
        s.is_default,
      ]),
    );
  }, [resolvedStatuses]);

  const categoriesKey = useMemo(() => {
    if (!resolvedCategories || resolvedCategories.length === 0) return "";
    return JSON.stringify(
      resolvedCategories.map((c: any) => [
        c.id,
        c.name,
        c.is_hidden,
        c.is_auto_select,
        c.is_default,
      ]),
    );
  }, [resolvedCategories]);

  const setIsCollapse = useCallback(
    (val: boolean) => {
      dispatch(
        setIsCollapseAction({
          isCollapse: val,
        }),
      );
    },
    [dispatch],
  );

  const triggerResetModalState = () => {
    setModalResetSignal((prev) => prev + 1);
  };

  const onCloseModalConfirm = (shouldReset = false) => {
    setModalConfirm({ isOpen: false, callback: null, content: "" });
    if (shouldReset) {
      triggerResetModalState();
      syncListDataFromApi();
    }
  };

  const handleModalConfirmCancel = () => {
    onCloseModalConfirm(true);
  };

  const handleCloseModal = () => {
    setModalSettingConfig({ isOpen: false, type: null });
  };

  const handleSubmit = async (
    newListData: CustomFilterConfigs[],
    listIdDeleted: number[],
  ) => {
    // check empty text
    if (newListData?.some((i: any) => !i?.text)) {
      notification["error"]({
        message:
          modalSettingConfig?.type === RECORD_TYPE.STATUS
            ? WORKER_SETTING_MESSAGES.STATUS_NAME_REQUIRED
            : WORKER_SETTING_MESSAGES.CATEGORY_NAME_REQUIRED,
      });
      return;
    }

    // check duplicate text
    const texts = newListData.map((i) => i?.text).filter(Boolean);
    const hasDuplicate = texts.length !== new Set(texts).size;

    if (hasDuplicate) {
      notification["error"]({
        message:
          modalSettingConfig?.type === RECORD_TYPE.STATUS
            ? WORKER_SETTING_MESSAGES.STATUS_NAME_DUPLICATED
            : WORKER_SETTING_MESSAGES.CATEGORY_NAME_DUPLICATED,
      });
      return;
    }

    const payload = {
      company_status_companies: newListData?.map((i, index) => ({
        ...(isNumber(i?.id) ? { id: i?.id } : {}),
        ...pick(i, ["is_hidden", "is_auto_select"]),
        sort_index: index,
        name: i?.text,
        record_type: modalSettingConfig?.type,
        screen: screen === SCREEN_NAME.LIST_MESS ? SCREEN_NAME.CHAT : screen,
      })),
      delete: listIdDeleted,
    };

    saveCategoryStatusCompanies(
      {
        payload,
      },
      {
        onSuccess() {
          queryClient.invalidateQueries([
            QUERY_NAME.CATEGORY_STATUS_USER_SETTINGS,
          ]);
          setModalSettingConfig({
            isOpen: false,
            type: null,
          });
        },
        onError(e: any) {
          notification["error"]({
            message: handleMessageError(e?.response?.data?.message),
          });

          setModalSettingConfig({
            isOpen: false,
            type: null,
          });
        },
      },
    );
  };

  const openConfirmModal = (
    listSelected: CustomFilterConfigs[],
    listIdDeleted: number[],
  ) => {
    const currentList =
      modalSettingConfig?.type === RECORD_TYPE.STATUS
        ? listDataStatus
        : modalSettingConfig?.type === RECORD_TYPE.CATEGORY
        ? listDataCategory
        : [];

    const hasUsedChanges = listSelected?.some((item) => {
      if (!isNumber(item?.id)) return false;
      const original = currentList?.find((orig) => orig?.id === item?.id);
      if (!original || original?.can_be_deleted !== false) return false;

      const sameText = original?.text === item?.text;
      const sameHidden =
        (original?.is_hidden ?? false) === (item?.is_hidden ?? false);
      const sameAuto =
        (original?.is_auto_select ?? false) === (item?.is_auto_select ?? false);

      return !(sameText && sameHidden && sameAuto);
    });

    if (hasUsedChanges) {
      setModalEditConfirm({
        isOpen: true,
        onConfirm: () => {
          handleSubmit(listSelected, listIdDeleted);
          setModalEditConfirm({
            isOpen: false,
            onConfirm: null,
            onCancel: null,
          });
        },
        onCancel: () => {
          notification["error"]({
            message: WORKER_SETTING_MESSAGES.CANNOT_EDIT_USED_VALUE,
          });
          triggerResetModalState();
          syncListDataFromApi();
          setModalEditConfirm({
            isOpen: false,
            onConfirm: null,
            onCancel: null,
          });
          handleCloseModal();
        },
      });
      return;
    }

    setModalConfirm({
      isOpen: true,
      callback: () => {
        handleSubmit(listSelected, listIdDeleted);
        onCloseModalConfirm(false);
      },
      content: listIdDeleted?.length
        ? WORKER_SETTING_MESSAGES.DELETE_CONFIRM_TITLE
        : WORKER_SETTING_MESSAGES.CONFIRM_CONTINUE_TITLE,
    });
  };

  // Helper to determine filter item value for status/category
  const resolveFilterItemValue = (
    item: any,
    optionDefault?: DefaultFilterOption[],
  ) => {
    const value = optionDefault?.find((x) => x.text === item.name)?.value;
    const isNull =
      item.name === SPECIAL_FILTER_LABEL.NULL ||
      item.value === SPECIAL_FILTER_VALUE.NULL;

    if (isNull) return SPECIAL_FILTER_VALUE.NULL;

    if (
      effectiveScreen === APP_TYPES.COMPANY_POLICIES ||
      effectiveScreen === SCREEN_NAME.CLIENT
    ) {
      return String(
        item.category_status_company_id || item.id || value || item.name,
      );
    } else if (effectiveScreen === APP_TYPES.BUSINESS_MANUALS) {
      return item.category_status_company_id || item.id || value || item.name;
    }

    return value || item.name;
  };

  const mapData = (
    apiList: (CategoryStatusCompany | CategoryStatusUserSettingItem)[],
    optionDefault?: DefaultFilterOption[],
  ): MappedItem[] => {
    if (apiList?.length) {
      return apiList.map((i: any) => {
        return {
          text: i.name,
          value: resolveFilterItemValue(i, optionDefault),
          selected: Boolean(i.is_auto_select),
          id: i.id,
          isDefault: !!(i.category_status_id || i.is_default),
          is_hidden: Boolean(i.is_hidden),
          is_auto_select: Boolean(i.is_auto_select),
          can_be_deleted: i.can_be_deleted,
          can_customize: i.can_customize,
          overrides: i.overrides,
          color: i.color || "#52C41A",
        };
      });
    }

    return (
      optionDefault?.map((option) => ({
        ...option,
        selected: false,
        id: option.id ?? undefined,
        isDefault: false,
        is_hidden: Boolean(option.is_hidden),
        is_auto_select: false,
      })) || []
    );
  };

  const updateSelected = (
    list: FilterSelected[],
    setter: React.Dispatch<React.SetStateAction<FilterSelected[]>>,
    val: string,
    status: FILTER_STATUS,
  ) => {
    setter((prev) => {
      const exist = prev.find((x) => x.value === val);

      if (exist) {
        return prev.map((item) =>
          item.value === val ? { ...item, status } : item,
        );
      }

      return [...prev, { value: val, status }];
    });
  };

  const handleChange = (val: string, isChecked: boolean, type: Type) => {
    if (disabled) return;

    if (type === RECORD_TYPE.CATEGORY) {
      hasUserModifiedCategoryRef.current = true;
      if (setCategoryFilterSelected) {
        updateSelected(
          categoryFilterSelected,
          setCategoryFilterSelected,
          val,
          isChecked ? FILTER_STATUS_TYPE.CHECKED : FILTER_STATUS_TYPE.UNCHECKED,
        );
        handleChangeCategory?.(val);
      }
      return;
    }

    if (type === RECORD_TYPE.STATUS) {
      hasUserModifiedStatusRef.current = true;
      if (setStatusFilterSelected) {
        updateSelected(
          statusFilterSelected,
          setStatusFilterSelected,
          val,
          isChecked ? FILTER_STATUS_TYPE.CHECKED : FILTER_STATUS_TYPE.UNCHECKED,
        );
        handleChangeStatus?.(val);
      }
    }
  };

  const handleChangeAll = (isChecked: boolean, type?: Type) => {
    if (disabled) return;

    if (type === RECORD_TYPE.CATEGORY) {
      hasUserModifiedCategoryRef.current = true;
      if (setCategoryFilterSelected) {
        const newSelected: FilterSelected[] = listDataCategory.map((item) => ({
          value: item.value,
          status: (isChecked
            ? FILTER_STATUS_TYPE.CHECKED
            : FILTER_STATUS_TYPE.UNCHECKED) as FILTER_STATUS,
          is_hidden: item.is_hidden,
          is_auto_select: item.is_auto_select,
        }));
        handleChangeCategory?.(isChecked ? "all" : "");
        setCategoryFilterSelected(newSelected);
      }
    }
    if (type === RECORD_TYPE.STATUS) {
      hasUserModifiedStatusRef.current = true;
      if (setStatusFilterSelected) {
        const newSelected: FilterSelected[] = listDataStatus.map((item) => ({
          value: item.value,
          status: (isChecked
            ? FILTER_STATUS_TYPE.CHECKED
            : FILTER_STATUS_TYPE.UNCHECKED) as FILTER_STATUS,
          is_hidden: item.is_hidden,
          is_auto_select: item.is_auto_select,
        }));
        handleChangeStatus?.(isChecked ? "all" : "");
        setStatusFilterSelected(newSelected);
      }
    }
  };

  const syncListDataFromApi = () => {
    // 1. Map statuses
    let mappedStatus: MappedItem[] = [];
    if (resolvedStatuses.length > 0) {
      mappedStatus = resolvedStatuses.map((status: any) => {
        return {
          text: status.name,
          value: resolveFilterItemValue(status, optionDefaultStatus),
          selected: Boolean(status.is_auto_select),
          id: status.id,
          isDefault: Boolean(
            status.is_default || status.isDefault || status.category_status_id,
          ),
          is_hidden: Boolean(status.is_hidden),
          is_auto_select: Boolean(status.is_auto_select),
          can_be_deleted: status.can_be_deleted ?? true,
          color: status.color || "#52C41A",
          can_customize: status.can_customize,
          overrides: status.overrides,
        };
      });
    } else if (optionDefaultStatus?.length) {
      mappedStatus = mapData([], optionDefaultStatus);
    }
    safeSetListDataStatus(mappedStatus);

    // 2. Map categories
    let mappedCategory: MappedItem[] = [];
    if (resolvedCategories.length > 0) {
      mappedCategory = resolvedCategories.map((category: any) => {
        return {
          text: category.name,
          value: resolveFilterItemValue(category, optionDefaultCategory),
          selected: Boolean(category.is_auto_select),
          id: category.id,
          isDefault: Boolean(
            category.is_default ||
              category.isDefault ||
              category.category_status_id,
          ),
          is_hidden: Boolean(category.is_hidden),
          is_auto_select: Boolean(category.is_auto_select),
          can_be_deleted: category.can_be_deleted ?? true,
          can_customize: category.can_customize,
          overrides: category.overrides,
        };
      });
    } else if (optionDefaultCategory?.length) {
      mappedCategory = mapData([], optionDefaultCategory);
    }
    safeSetListDataCategory(mappedCategory);

    // If chat with preFetchedChatStatuses / preFetchedChatCategories, ListMess hook manages filter selections
    const isChatWithProps =
      isListMess &&
      (Boolean(preFetchedChatStatuses?.length) ||
        Boolean(preFetchedChatCategories?.length));
    if (isChatWithProps) {
      return;
    }

    // 3. Process Status Filter Selection
    if (setStatusFilterSelected && mappedStatus.length > 0) {
      if (!isInitializedStatusRef.current) {
        // Initialize from BE user personalized settings
        const visible = mappedStatus.filter((x) => !x.is_hidden);
        const hasAuto = visible.some((x) => x.is_auto_select);
        const hasOverride = visible.some(
          (x: any) =>
            x.overrides?.is_auto_select !== null &&
            x.overrides?.is_auto_select !== undefined,
        );

        const initialFilter: FilterSelected[] = mappedStatus.map((x) => {
          const existingItem = statusFilterSelected?.find(
            (it: any) =>
              String(it.value) === String(x.value) ||
              String(it.value) === String(x.text) ||
              String(it.label) === String(x.text) ||
              String(it.label) === String(x.value) ||
              (it.id && x.id && String(it.id) === String(x.id)),
          );

          let isChecked = false;
          if (resolvedStatuses.length > 0) {
            if (hasAuto) {
              isChecked = Boolean(x.is_auto_select);
            } else if (hasOverride) {
              isChecked = false;
            } else if (existingItem) {
              isChecked =
                existingItem.status === FILTER_STATUS_TYPE.CHECKED ||
                existingItem.status === FILTER_STATUS_TYPE.SELECTED;
            } else if (
              forceCheckAllIfEmpty &&
              statusFilterSelected.length === 0
            ) {
              isChecked = true;
            } else {
              isChecked = true;
            }
          } else {
            if (existingItem) {
              isChecked =
                existingItem.status === FILTER_STATUS_TYPE.CHECKED ||
                existingItem.status === FILTER_STATUS_TYPE.SELECTED;
            } else if (hasAuto) {
              isChecked = Boolean(x.is_auto_select);
            } else {
              isChecked = true;
            }
          }

          if (x.id) {
            initialStatusAutoSelectRef.current[x.id] = isChecked;
          }

          return {
            value: x.value,
            status: isChecked
              ? FILTER_STATUS_TYPE.CHECKED
              : FILTER_STATUS_TYPE.UNCHECKED,
            is_hidden: Boolean(x.is_hidden),
            is_auto_select: Boolean(x.is_auto_select),
          };
        });

        setStatusFilterSelected(initialFilter);
        initialStatusSnapshotRef.current = JSON.stringify(
          initialFilter.map((x) => ({ value: x.value, status: x.status })),
        );
        isInitializedStatusRef.current = true;
      } else {
        // Preserve user selections for active screen
        const filteredStatus = mappedStatus.map((x) => {
          const item = statusFilterSelected.find(
            (it: any) =>
              String(it.value) === String(x.value) ||
              String(it.value) === String(x.text) ||
              String(it.label) === String(x.text) ||
              String(it.label) === String(x.value) ||
              (it.id && x.id && String(it.id) === String(x.id)),
          );
          return {
            value: x.value,
            status: item
              ? item.status
              : x.is_auto_select
              ? FILTER_STATUS_TYPE.CHECKED
              : FILTER_STATUS_TYPE.UNCHECKED,
            is_hidden: x.is_hidden,
            is_auto_select: x.is_auto_select,
          };
        });

        setStatusFilterSelected((prev) =>
          isEqual(prev, filteredStatus) ? prev : filteredStatus,
        );
      }
    }

    // 4. Process Category Filter Selection
    if (setCategoryFilterSelected && mappedCategory.length > 0) {
      if (!isInitializedCategoryRef.current) {
        // Initialize from BE user personalized settings
        const visible = mappedCategory.filter((x) => !x.is_hidden);
        const hasAuto = visible.some((x) => x.is_auto_select);
        const hasOverride = visible.some(
          (x: any) =>
            x.overrides?.is_auto_select !== null &&
            x.overrides?.is_auto_select !== undefined,
        );

        const initialFilter: FilterSelected[] = mappedCategory.map((x) => {
          const existingItem = categoryFilterSelected?.find(
            (it: any) =>
              String(it.value) === String(x.value) ||
              String(it.value) === String(x.text) ||
              String(it.label) === String(x.text) ||
              String(it.label) === String(x.value) ||
              (it.id && x.id && String(it.id) === String(x.id)),
          );

          let isChecked = false;
          if (resolvedCategories.length > 0) {
            if (hasAuto) {
              isChecked = Boolean(x.is_auto_select);
            } else if (hasOverride) {
              isChecked = false;
            } else if (existingItem) {
              isChecked =
                existingItem.status === FILTER_STATUS_TYPE.CHECKED ||
                existingItem.status === FILTER_STATUS_TYPE.SELECTED;
            } else if (
              forceCheckAllIfEmpty &&
              categoryFilterSelected.length === 0
            ) {
              isChecked = true;
            } else {
              isChecked = true;
            }
          } else {
            if (existingItem) {
              isChecked =
                existingItem.status === FILTER_STATUS_TYPE.CHECKED ||
                existingItem.status === FILTER_STATUS_TYPE.SELECTED;
            } else if (hasAuto) {
              isChecked = Boolean(x.is_auto_select);
            } else {
              isChecked = true;
            }
          }

          if (x.id) {
            initialCategoryAutoSelectRef.current[x.id] = isChecked;
          }

          return {
            value: x.value,
            status: isChecked
              ? FILTER_STATUS_TYPE.CHECKED
              : FILTER_STATUS_TYPE.UNCHECKED,
            is_hidden: Boolean(x.is_hidden),
            is_auto_select: Boolean(x.is_auto_select),
          };
        });

        setCategoryFilterSelected(initialFilter);
        initialCategorySnapshotRef.current = JSON.stringify(
          initialFilter.map((x) => ({ value: x.value, status: x.status })),
        );
        isInitializedCategoryRef.current = true;
      } else {
        // Preserve user selections for active screen
        const filteredCategory = mappedCategory.map((x) => {
          const item = categoryFilterSelected.find(
            (it: any) =>
              String(it.value) === String(x.value) ||
              String(it.value) === String(x.text) ||
              String(it.label) === String(x.text) ||
              String(it.label) === String(x.value) ||
              (it.id && x.id && String(it.id) === String(x.id)),
          );
          return {
            value: x.value,
            status: item
              ? item.status
              : x.is_auto_select
              ? FILTER_STATUS_TYPE.CHECKED
              : FILTER_STATUS_TYPE.UNCHECKED,
            is_hidden: x.is_hidden,
            is_auto_select: x.is_auto_select,
          };
        });

        setCategoryFilterSelected((prev) =>
          isEqual(prev, filteredCategory) ? prev : filteredCategory,
        );
      }
    }
  };

  // Keep latest references for auto-save
  const latestRef = useRef({
    companyId,
    effectiveScreen,
    listDataStatus,
    listDataCategory,
    statusFilterSelected,
    categoryFilterSelected,
  });

  useEffect(() => {
    latestRef.current = {
      companyId,
      effectiveScreen,
      listDataStatus,
      listDataCategory,
      statusFilterSelected,
      categoryFilterSelected,
    };
  }, [
    companyId,
    effectiveScreen,
    listDataStatus,
    listDataCategory,
    statusFilterSelected,
    categoryFilterSelected,
  ]);

  // Save status settings via mutation hook
  const saveStatusSettings = useCallback(() => {
    const {
      companyId: cId,
      effectiveScreen: scr,
      listDataStatus: curStatusList,
      statusFilterSelected: curStatusFilter,
    } = latestRef.current;

    if (!cId || !scr || curStatusList.length === 0) return;

    const isChatWithProps =
      (scr === SCREEN_NAME.CHAT || scr === SCREEN_NAME.LIST_MESS) &&
      (Boolean(preFetchedChatStatuses?.length) ||
        Boolean(preFetchedChatCategories?.length));
    if (isChatWithProps) return;

    const statusSettings: CategoryStatusUserSettingPayloadItem[] = [];

    curStatusList.forEach((item: any) => {
      if (!item.id && !item.value && !item.text && !item.name) return;

      const curItem = curStatusFilter.find(
        (s: any) =>
          (s.id && item.id && String(s.id) === String(item.id)) ||
          String(s.value) === String(item.value || item.text) ||
          String(s.value) === String(item.name) ||
          (s.label && String(s.label) === String(item.name)) ||
          ((item.name === SPECIAL_FILTER_LABEL.NULL ||
            item.text === SPECIAL_FILTER_LABEL.NULL ||
            item.value === SPECIAL_FILTER_VALUE.NULL) &&
            (s.value === SPECIAL_FILTER_VALUE.NULL ||
              s.value === SPECIAL_FILTER_LABEL.NULL ||
              s.text === SPECIAL_FILTER_LABEL.NULL ||
              s.label === SPECIAL_FILTER_LABEL.NULL)),
      );

      const isChecked = curItem
        ? curItem.status === FILTER_STATUS_TYPE.CHECKED ||
          curItem.status === FILTER_STATUS_TYPE.SELECTED
        : Boolean(item.is_auto_select);

      statusSettings.push({
        ...(item.id ? { category_status_company_id: Number(item.id) } : {}),
        name: item.text || item.name,
        value: item.value,
        is_auto_select: isChecked,
      });
    });

    const uniqueSettings = uniqBy(
      statusSettings,
      (s) => s.category_status_company_id ?? s.value ?? s.name,
    );
    if (uniqueSettings.length === 0) return;

    const payload: SaveCategoryStatusUserSettingsPayload = {
      company_id: cId,
      screen: scr === SCREEN_NAME.LIST_MESS ? SCREEN_NAME.CHAT : scr,
      record_type: RECORD_TYPE.STATUS,
      settings: uniqueSettings,
    };

    saveUserSettingsMutation(
      { payload },
      {
        onSuccess: () => {
          hasUserModifiedStatusRef.current = false;
        },
        onError: (err) => {
          console.error("Failed to auto-save status user settings", err);
        },
      },
    );
  }, [
    preFetchedChatStatuses,
    preFetchedChatCategories,
    saveUserSettingsMutation,
  ]);

  // Save category settings via mutation hook
  const saveCategorySettings = useCallback(() => {
    const {
      companyId: cId,
      effectiveScreen: scr,
      listDataCategory: curCategoryList,
      categoryFilterSelected: curCategoryFilter,
    } = latestRef.current;

    if (!cId || !scr || curCategoryList.length === 0) return;

    const isChatWithProps =
      (scr === SCREEN_NAME.CHAT || scr === SCREEN_NAME.LIST_MESS) &&
      (Boolean(preFetchedChatStatuses?.length) ||
        Boolean(preFetchedChatCategories?.length));
    if (isChatWithProps) return;

    const categorySettings: CategoryStatusUserSettingPayloadItem[] = [];

    curCategoryList.forEach((item: any) => {
      if (!item.id && !item.value && !item.text && !item.name) return;

      const curItem = curCategoryFilter.find(
        (c: any) =>
          (c.id && item.id && String(c.id) === String(item.id)) ||
          String(c.value) === String(item.value || item.text) ||
          String(c.value) === String(item.name) ||
          (c.label && String(c.label) === String(item.name)) ||
          ((item.name === SPECIAL_FILTER_LABEL.NULL ||
            item.text === SPECIAL_FILTER_LABEL.NULL ||
            item.value === SPECIAL_FILTER_VALUE.NULL) &&
            (c.value === SPECIAL_FILTER_VALUE.NULL ||
              c.value === SPECIAL_FILTER_LABEL.NULL ||
              c.text === SPECIAL_FILTER_LABEL.NULL ||
              c.label === SPECIAL_FILTER_LABEL.NULL)),
      );

      const isChecked = curItem
        ? curItem.status === FILTER_STATUS_TYPE.CHECKED ||
          curItem.status === FILTER_STATUS_TYPE.SELECTED
        : Boolean(item.is_auto_select);

      categorySettings.push({
        ...(item.id ? { category_status_company_id: Number(item.id) } : {}),
        name: item.text || item.name,
        value: item.value,
        is_auto_select: isChecked,
      });
    });

    const uniqueSettings = uniqBy(
      categorySettings,
      (c) => c.category_status_company_id ?? c.value ?? c.name,
    );
    if (uniqueSettings.length === 0) return;

    const payload: SaveCategoryStatusUserSettingsPayload = {
      company_id: cId,
      screen: scr === SCREEN_NAME.LIST_MESS ? SCREEN_NAME.CHAT : scr,
      record_type: RECORD_TYPE.CATEGORY,
      settings: uniqueSettings,
    };

    saveUserSettingsMutation(
      { payload },
      {
        onSuccess: () => {
          hasUserModifiedCategoryRef.current = false;
        },
        onError: (err) => {
          console.error("Failed to auto-save category user settings", err);
        },
      },
    );
  }, [
    preFetchedChatStatuses,
    preFetchedChatCategories,
    saveUserSettingsMutation,
  ]);

  // Debounced auto-save on status selection change
  useEffect(() => {
    if (!isInitializedStatusRef.current) return;
    const currentSnapshot = JSON.stringify(
      statusFilterSelected.map((x) => ({ value: x.value, status: x.status })),
    );
    if (
      initialStatusSnapshotRef.current &&
      currentSnapshot === initialStatusSnapshotRef.current
    ) {
      return;
    }

    hasUserModifiedStatusRef.current = true;

    if (statusDebounceTimerRef.current) {
      clearTimeout(statusDebounceTimerRef.current);
    }
    statusDebounceTimerRef.current = setTimeout(() => {
      saveStatusSettings();
      initialStatusSnapshotRef.current = currentSnapshot;
    }, 400);

    return () => {
      if (statusDebounceTimerRef.current) {
        clearTimeout(statusDebounceTimerRef.current);
      }
    };
  }, [statusFilterSelected, saveStatusSettings]);

  // Debounced auto-save on category selection change
  useEffect(() => {
    if (!isInitializedCategoryRef.current) return;
    const currentSnapshot = JSON.stringify(
      categoryFilterSelected.map((x) => ({
        value: x.value,
        status: x.status,
      })),
    );
    if (
      initialCategorySnapshotRef.current &&
      currentSnapshot === initialCategorySnapshotRef.current
    ) {
      return;
    }

    hasUserModifiedCategoryRef.current = true;

    if (categoryDebounceTimerRef.current) {
      clearTimeout(categoryDebounceTimerRef.current);
    }
    categoryDebounceTimerRef.current = setTimeout(() => {
      saveCategorySettings();
      initialCategorySnapshotRef.current = currentSnapshot;
    }, 400);

    return () => {
      if (categoryDebounceTimerRef.current) {
        clearTimeout(categoryDebounceTimerRef.current);
      }
    };
  }, [categoryFilterSelected, saveCategorySettings]);

  useEffect(() => {
    if (isStatusesLoading || isCategoriesLoading) {
      return;
    }

    syncListDataFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusesKey,
    categoriesKey,
    isStatusesLoading,
    isCategoriesLoading,
    isSuccessCategoryStatusCompaniesData,
  ]);

  useEffect(() => {
    if (isDisplayInNewTab) {
      setIsCollapse(true);
    }
  }, [isDisplayInNewTab, setIsCollapse]);

  return {
    ...props,
    listDataStatus: uniqBy(listDataStatus, "text"),
    setListDataStatus,
    listDataCategory: uniqBy(listDataCategory, "text"),
    setListDataCategory,
    modalSettingConfig,
    setModalSettingConfig,
    handleCloseModal,
    handleSubmit,
    optionDefaultStatus,
    optionDefaultCategory,
    isSavingCategoryStatusCompanies,
    isCollapse,
    setIsCollapse,
    isLoadingCategoryStatusCompaniesData:
      isStatusesLoading || isCategoriesLoading,
    isFetchingCategoryStatusCompaniesData: isListMess
      ? false
      : isFetchingCategoryStatusCompaniesData,
    isCollapseSidebar,
    nonEditable,
    modalConfirm,
    setModalConfirm,
    onCancelModalConfirm: handleModalConfirmCancel,
    disabledButtonEdit,
    modalEditConfirm,
    openConfirmModal,
    modalResetSignal,
    handleChange,
    maintenanceBannerHeight,
    displaySize,
    height,
    isDisplayInNewTab,
    handleChangeAll,
    showConfigControls,
    hasNullStatusOption,
  };
};

export type Props = ReturnType<typeof useSearchAreaBox>;

export default useSearchAreaBox;
